import type { FieldPath } from 'firebase/firestore';
import type { CollectionEntry } from '../types';
import {
  collectionBagEntryFieldPairs,
  collectionBagHeaderFields,
} from './collectionMutation';

/**
 * How long a patch may sit unwritten. Long enough that a held-down quantity
 * spinner or a chunked CSV import collapses into one write, short enough that
 * a tab killed without warning loses at most this much.
 */
export const OUTBOX_FLUSH_DELAY_MS = 400;

/**
 * Cards per bag update. A Firestore update is one billed write no matter how
 * many fields it sets, so this exists only to keep a request under the 1 MiB
 * transport cap — not to save writes.
 */
export const OUTBOX_MAX_CARDS_PER_UPDATE = 400;

/**
 * Pending cards past which waiting stops making sense: the batch is already
 * worth sending and holding more only widens the window for losing it.
 */
export const OUTBOX_FLUSH_THRESHOLD = OUTBOX_MAX_CARDS_PER_UPDATE;

/** One `update` call's worth of vararg field pairs. */
export type OutboxUpdateChunk = Array<string | FieldPath | unknown>;

/**
 * Split dirty cards into update payloads.
 *
 * Each chunk repeats the bag header, so any single chunk landing alone still
 * leaves the document at a coherent version and revision. Cards absent from
 * `entries` are emitted as deletes, which is how a removal reaches Firestore.
 */
export function planOutboxUpdates(
  entries: Record<string, CollectionEntry>,
  dirtyCardIds: Iterable<string>,
  revision: number,
  maxCardsPerUpdate = OUTBOX_MAX_CARDS_PER_UPDATE,
): OutboxUpdateChunk[] {
  const ids = [...dirtyCardIds];
  if (ids.length === 0) return [];

  const chunks: OutboxUpdateChunk[] = [];
  for (let i = 0; i < ids.length; i += maxCardsPerUpdate) {
    const slice = ids.slice(i, i + maxCardsPerUpdate);
    const fields: OutboxUpdateChunk = collectionBagHeaderFields(revision);
    for (const cardId of slice) {
      fields.push(...collectionBagEntryFieldPairs(cardId, entries[cardId]));
    }
    chunks.push(fields);
  }
  return chunks;
}

/**
 * Accumulates card ids whose bag rows have diverged from Firestore.
 *
 * Deliberately holds ids, not patches: the provider already applies each patch
 * to its in-memory entries, so the row to write is whatever `entries` says at
 * flush time. That makes coalescing exact — five taps on one card write once,
 * with the final value — and makes a re-queued failure pick up any edit that
 * happened while the failed write was in flight.
 */
export class CollectionOutbox {
  private dirty = new Set<string>();

  /** Cards awaiting a write. */
  get size(): number {
    return this.dirty.size;
  }

  get pendingCardIds(): string[] {
    return [...this.dirty];
  }

  add(cardId: string): void {
    this.dirty.add(cardId);
  }

  /** Whether a flush should not wait for the debounce. */
  shouldFlushNow(threshold = OUTBOX_FLUSH_THRESHOLD): boolean {
    return this.dirty.size >= threshold;
  }

  /**
   * Hand over the pending ids and clear. Mutations that arrive during the
   * write land in the fresh set and are written by the next flush.
   */
  drain(): string[] {
    const ids = [...this.dirty];
    this.dirty.clear();
    return ids;
  }

  /** Put drained ids back after a failed write, without dropping newer ones. */
  requeue(cardIds: Iterable<string>): void {
    for (const id of cardIds) this.dirty.add(id);
  }

  clear(): void {
    this.dirty.clear();
  }
}
